import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiAlertCircle, FiLoader, FiLock, FiX } from 'react-icons/fi';
import { formatKES, withVat, toPaystackAmount, TEST_CHARGE_AMOUNT_KES } from '../../lib/tax';
import { loadPaystackInline, generateReference } from '../../lib/paystack';

const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

export default function CheckoutModal({ open, onClose, pkg, accountType, email, onVerify }) {
  const [stage, setStage] = useState('summary'); // summary | paying | verifying | error
  const [errorMessage, setErrorMessage] = useState('');

  if (!open || !pkg) return null;

  const { subtotal, vat, total } = withVat(pkg.Amount);
  const lineItem = `${accountType === 'organization' ? 'Organization' : 'Individual'} — ${pkg.PackageName} (1 month)`;

  const handleCheckout = async () => {
    setErrorMessage('');
    setStage('paying');

    try {
      const PaystackPop = await loadPaystackInline();
      const reference = generateReference();

      PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email,
        // TEMPORARY: the customer is quoted and shown `total` below, but the live Paystack
        // integration is being tested cheaply — remove this override before going live.
        amount: toPaystackAmount(TEST_CHARGE_AMOUNT_KES),
        currency: 'KES',
        ref: reference,
        metadata: { accountType, packageId: pkg.Id, packageName: pkg.PackageName },
        callback: (response) => {
          setStage('verifying');
          onVerify(response.reference).catch((err) => {
            setErrorMessage(err.message || 'Payment succeeded but we could not finish creating your account.');
            setStage('error');
          });
        },
        onClose: () => {
          setStage((current) => (current === 'verifying' ? current : 'summary'));
        },
      }).openIframe();
    } catch (err) {
      setErrorMessage(err.message || 'Could not start checkout.');
      setStage('error');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-midnight/60 p-4 backdrop-blur-sm"
        onClick={() => stage === 'summary' && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.97 }}
          onClick={(e) => e.stopPropagation()}
          className="card w-full max-w-md p-8"
        >
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-lg font-bold text-ink dark:text-white">Order summary</h3>
            {stage === 'summary' && (
              <button onClick={onClose} aria-label="Close" className="text-subtle hover:text-ink dark:hover:text-white">
                <FiX size={20} />
              </button>
            )}
          </div>

          {stage === 'error' && (
            <div className="mb-5 flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/5 p-4 text-sm text-danger">
              <FiAlertCircle className="mt-0.5 flex-none" size={16} />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="flex flex-col gap-3 border-b border-line pb-5 text-sm dark:border-midnight-600">
            <div className="flex justify-between text-muted dark:text-subtle">
              <span>{lineItem}</span>
              <span>{formatKES(subtotal)}</span>
            </div>
            <div className="flex justify-between text-muted dark:text-subtle">
              <span>Subtotal</span>
              <span>{formatKES(subtotal)}</span>
            </div>
            <div className="flex justify-between text-muted dark:text-subtle">
              <span>VAT (16%)</span>
              <span>{formatKES(vat)}</span>
            </div>
          </div>

          <div className="flex justify-between py-5 text-base font-bold text-ink dark:text-white">
            <span>Total</span>
            <span>{formatKES(total)}</span>
          </div>

          <button
            type="button"
            onClick={handleCheckout}
            disabled={stage === 'paying' || stage === 'verifying'}
            className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-70"
          >
            {stage === 'verifying' ? (
              <>
                <FiLoader className="animate-spin" /> Finalizing your account…
              </>
            ) : stage === 'paying' ? (
              <>
                <FiLoader className="animate-spin" /> Waiting for payment…
              </>
            ) : (
              <>
                <FiLock /> Checkout with Paystack
              </>
            )}
          </button>

          <p className="mt-4 text-center text-xs text-subtle">
            Payment is processed securely by Paystack. Your account is created only after payment is verified.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
